export type Comparator<T> = (left: T, right: T) => number;

export class PriorityQueue<T> {
  readonly #items: T[] = [];
  readonly #compare: Comparator<T>;

  constructor(compare: Comparator<T>) {
    this.#compare = compare;
  }

  get size(): number {
    return this.#items.length;
  }

  peek(): T | undefined {
    return this.#items[0];
  }

  push(value: T): void {
    this.#items.push(value);
    this.#bubbleUp(this.#items.length - 1);
  }

  pop(): T | undefined {
    if (this.#items.length === 0) return undefined;
    const root = this.#items[0];
    const tail = this.#items.pop();
    if (this.#items.length > 0 && tail !== undefined) {
      this.#items[0] = tail;
      this.#sinkDown(0);
    }
    return root;
  }

  toArray(): T[] {
    return [...this.#items];
  }

  #bubbleUp(startIndex: number): void {
    let index = startIndex;
    while (index > 0) {
      const parentIndex = Math.floor((index - 1) / 2);
      if (this.#compare(this.#items[index] as T, this.#items[parentIndex] as T) >= 0) break;
      [this.#items[index], this.#items[parentIndex]] = [this.#items[parentIndex] as T, this.#items[index] as T];
      index = parentIndex;
    }
  }

  #sinkDown(startIndex: number): void {
    let index = startIndex;
    while (true) {
      const leftIndex = index * 2 + 1;
      const rightIndex = leftIndex + 1;
      let best = index;
      if (leftIndex < this.#items.length && this.#compare(this.#items[leftIndex] as T, this.#items[best] as T) < 0) best = leftIndex;
      if (rightIndex < this.#items.length && this.#compare(this.#items[rightIndex] as T, this.#items[best] as T) < 0) best = rightIndex;
      if (best === index) return;
      [this.#items[index], this.#items[best]] = [this.#items[best] as T, this.#items[index] as T];
      index = best;
    }
  }
}
